import React, { useState, useEffect } from 'react';
import { DepartmentNav } from '../components/DepartmentNav';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Calculator, Flask, Drop, Eyedropper, ArrowRight, ShoppingCart, Info, Warning, CheckCircle, Gauge } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../config/api';
import { SEO } from '../components/SEO';
import { PageWhy } from '../components/PageWhy';
import { SpoonFeedCalculator } from '../components/SpoonFeedCalculator';
import { AmazonDisclosure } from '../components/AmazonDisclosure';

// Recommended 4-gallon sprayer affiliate link
const SPRAYER_LINK = "https://www.amazon.com/s?k=chapin+20541+4-gallon+backpack+sprayer";
const SPRAYER_NAME = "Chapin 20541 4-Gallon Backpack Sprayer";

export const MixCalculatorPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const isFreeUser = user?.subscription_tier === 'free' && user?.role !== 'admin';

  // Combo calculator state
  const [areaSqft, setAreaSqft] = useState('');
  const [sprayerGallons, setSprayerGallons] = useState('4');
  const [celsiusRate, setCelsiusRate] = useState('medium');
  const [certaintyRate, setCertaintyRate] = useState('medium');
  const [includeSurfactant, setIncludeSurfactant] = useState(true);
  const [includeDye, setIncludeDye] = useState(true);
  const [comboResult, setComboResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  // Season Pace
  const [seasonPace, setSeasonPace] = useState(null);
  const [paceLoading, setPaceLoading] = useState(false);

  // Fertilizer calculator state
  const [fertArea, setFertArea] = useState('');
  const [fertN, setFertN] = useState('');
  const [fertP, setFertP] = useState('');
  const [fertK, setFertK] = useState('');
  const [fertBagN, setFertBagN] = useState('');
  const [fertBagP, setFertBagP] = useState('');
  const [fertBagK, setFertBagK] = useState('');
  const [fertBagWeight, setFertBagWeight] = useState('50');
  const [fertResult, setFertResult] = useState(null);

  // Products for shopping links
  const [adjuncts, setAdjuncts] = useState([]);

  useEffect(() => {
    fetchProducts();
    if (user) fetchSeasonPace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/calculator/products`);
      setAdjuncts(res.data.adjuncts || []);
    } catch (err) {
      console.error('Failed to fetch products', err);
    }
  };

  const fetchSeasonPace = async () => {
    setPaceLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/gdd/season-pace`, { withCredentials: true });
      setSeasonPace(res.data);
    } catch (err) {
      // Non-blocking — user may not have location set
      console.error('Season pace unavailable', err);
    } finally {
      setPaceLoading(false);
    }
  };

  const calculateFertilizer = () => {
    if (!fertArea || !fertBagN) {
      toast.error('Enter your lawn area and fertilizer bag analysis');
      return;
    }
    const area = parseFloat(fertArea);
    const bagN = parseFloat(fertBagN) / 100;
    const bagP = parseFloat(fertBagP || 0) / 100;
    const bagK = parseFloat(fertBagK || 0) / 100;
    const bagWeight = parseFloat(fertBagWeight || 50);
    const targetN = parseFloat(fertN || 1); // lbs N per 1000 sq ft (default 1 lb)
    const targetP = parseFloat(fertP || 0);
    const targetK = parseFloat(fertK || 0);

    // How much product per 1000 sq ft to hit N target
    const productPer1K = bagN > 0 ? targetN / bagN : 0;
    // Total product needed
    const areaUnits = area / 1000;
    const totalProduct = productPer1K * areaUnits;
    // How many bags
    const bagsNeeded = totalProduct / bagWeight;
    // Actual P and K applied at this rate
    const actualP = productPer1K * bagP;
    const actualK = productPer1K * bagK;

    setFertResult({
      productPer1K: productPer1K.toFixed(2),
      totalProduct: totalProduct.toFixed(1),
      bagsNeeded: Math.ceil(bagsNeeded * 10) / 10,
      actualN: targetN,
      actualP: actualP.toFixed(2),
      actualK: actualK.toFixed(2),
      area,
      bagAnalysis: `${fertBagN}-${fertBagP || 0}-${fertBagK || 0}`,
    });
  };


  const calculateCombo = async () => {
    if (!areaSqft || parseFloat(areaSqft) <= 0) {
      toast.error('Please enter your lawn area');
      return;
    }
    setCalculating(true);
    try {
      const res = await axios.post(`${API_URL}/api/calculator/combo-mix`, {
        area_sqft: parseFloat(areaSqft),
        sprayer_gallons: parseFloat(sprayerGallons),
        celsius_rate: celsiusRate,
        certainty_rate: certaintyRate,
        include_surfactant: includeSurfactant,
        include_dye: includeDye,
      }, { withCredentials: true });
      setComboResult(res.data);
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.detail?.upgrade_required) {
        toast.error('Mix Calculator requires a Homeowner Seasonal subscription', {
          action: { label: 'Upgrade', onClick: () => navigate('/app') },
          duration: 6000,
        });
      } else {
        toast.error('Calculation failed');
      }
    } finally {
      setCalculating(false);
    }
  };

  const paceColor = seasonPace?.pace === 'ahead of schedule' ? 'text-amber-600' : seasonPace?.pace === 'behind schedule' ? 'text-blue-600' : 'text-green-600';
  const paceBg = seasonPace?.pace === 'ahead of schedule' ? 'bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800/40' : seasonPace?.pace === 'behind schedule' ? 'bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800/40' : 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800/40';

  return (
    <div data-testid="mix-calculator-page" className="min-h-screen bg-background">
      <SEO
        title="Tank Mix & Fertilizer Calculator"
        description="Calculate exact product rates for your lawn size. Tank mix calculator, granular fertilizer spreader rates, and liquid spray mix ratios. Built for homeowners."
        keywords="tank mix calculator, fertilizer calculator, lawn product rate calculator, herbicide mix ratio, granular spreader rate, liquid spray rate calculator"
        path="/mix-calculator"
        imageAlt="TurfPulse Mix Calculator — Precise product rate calculations for your lawn"
      />
      <DepartmentNav />
      <div className="max-w-5xl mx-auto p-6 space-y-6">

        {/* Upgrade Banner for Free Users */}
        {isFreeUser && (
          <div data-testid="calc-upgrade-banner" className="rounded-lg border-2 border-primary/30 bg-primary/5 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-sm">Upgrade to unlock the Mix Calculator</p>
              <p className="text-xs text-muted-foreground mt-0.5">Get exact grams, oz, and scoops for every product. Homeowner Seasonal — $50/season.</p>
            </div>
            <Button size="sm" onClick={() => navigate('/app')} data-testid="calc-upgrade-btn">
              Upgrade Now
            </Button>
          </div>
        )}

        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold flex items-center gap-2" data-testid="calc-page-title">
              <Flask weight="duotone" size={28} className="text-primary" />
              Mix Calculator
            </h2>
            <PageWhy testId="calc-page-why">Dial in the exact product amount for your lawn size — so you don't over-apply, waste product, or burn your turf.</PageWhy>
          </div>
        </div>

        {/* Season Pace Widget */}
        {seasonPace && (
          <Card className={`border ${paceBg}`} data-testid="season-pace-widget">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <Gauge weight="duotone" size={28} className={paceColor} />
                <div className="flex-1">
                  <p className={`font-semibold ${paceColor}`}>
                    Season Pace: {seasonPace.weeks_difference > 0 ? `${seasonPace.weeks_difference} weeks ${seasonPace.pace}` : 'On schedule'}
                  </p>
                  <p className="text-sm text-muted-foreground">{seasonPace.message}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold">{seasonPace.this_year_gdd} GDD</p>
                  <p className="text-xs text-muted-foreground">vs {seasonPace.last_year_gdd} last year</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="combo" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="combo" data-testid="tab-combo">Herbicide Combo</TabsTrigger>
            <TabsTrigger value="fertilizer" data-testid="tab-fertilizer">Fertilizer</TabsTrigger>
            <TabsTrigger value="spoonfeed" data-testid="tab-spoonfeed">Spoon-Feed</TabsTrigger>
            <TabsTrigger value="shopping" data-testid="tab-shopping">Shopping List</TabsTrigger>
          </TabsList>

          {/* ============ COMBO CALCULATOR ============ */}
          <TabsContent value="combo">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Left: Inputs */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator weight="duotone" size={22} className="text-primary" />
                    Your Spray Setup
                  </CardTitle>
                  <CardDescription>
                    Enter your lawn area and sprayer size — we'll do the math
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Label Disclaimer */}
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40" data-testid="calc-disclaimer">
                    <Warning weight="fill" size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-800 dark:text-amber-300">
                      <strong>Always read and follow the product label.</strong> The label is the law. This calculator provides general guidance — defer to the manufacturer's label for exact instructions. Safe on Bermuda, Zoysia, St. Augustine, and Centipede. <strong>NOT for Bahia or cool-season grasses.</strong>
                    </p>
                  </div>

                  {/* Lawn Area */}
                  <div>
                    <Label htmlFor="area-input">Lawn Area (sq ft)</Label>
                    <Input
                      id="area-input"
                      data-testid="calc-area-input"
                      type="number"
                      min="100"
                      step="100"
                      value={areaSqft}
                      onChange={(e) => setAreaSqft(e.target.value)}
                      placeholder="e.g., 5000"
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Don't know your area? Use Google Earth or pace it out (1 pace = ~2.5 ft)
                    </p>
                  </div>

                  {/* Sprayer Size */}
                  <div>
                    <Label htmlFor="sprayer-input">Sprayer Size (gallons)</Label>
                    <Select value={sprayerGallons} onValueChange={setSprayerGallons}>
                      <SelectTrigger data-testid="calc-sprayer-select" className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 Gallon (Pump Sprayer)</SelectItem>
                        <SelectItem value="2">2 Gallon (Pump Sprayer)</SelectItem>
                        <SelectItem value="4">4 Gallon (Backpack Sprayer)</SelectItem>
                        <SelectItem value="5">5 Gallon (Large Backpack)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Rate Selectors */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Celsius WG Rate</Label>
                      <Select value={celsiusRate} onValueChange={setCelsiusRate}>
                        <SelectTrigger data-testid="calc-celsius-rate" className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low (0.057 oz/1K)</SelectItem>
                          <SelectItem value="medium">Medium (0.085 oz/1K)</SelectItem>
                          <SelectItem value="high">High (0.113 oz/1K)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Certainty Rate</Label>
                      <Select value={certaintyRate} onValueChange={setCertaintyRate}>
                        <SelectTrigger data-testid="calc-certainty-rate" className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low (0.023 oz/1K)</SelectItem>
                          <SelectItem value="medium">Medium (0.04 oz/1K)</SelectItem>
                          <SelectItem value="high">High (0.057 oz/1K)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Toggles */}
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={includeSurfactant} onChange={(e) => setIncludeSurfactant(e.target.checked)} className="rounded" data-testid="calc-surfactant-toggle" />
                      <span className="text-sm">Add Surfactant</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={includeDye} onChange={(e) => setIncludeDye(e.target.checked)} className="rounded" data-testid="calc-dye-toggle" />
                      <span className="text-sm">Add Spray Dye</span>
                    </label>
                  </div>

                  {/* Calculate Button */}
                  <Button
                    onClick={calculateCombo}
                    disabled={calculating || !areaSqft}
                    className="w-full"
                    size="lg"
                    data-testid="calc-calculate-btn"
                  >
                    <Calculator weight="bold" size={20} className="mr-2" />
                    {calculating ? 'Calculating...' : 'Calculate My Mix'}
                  </Button>
                </CardContent>
              </Card>

              {/* Right: Results */}
              <div className="space-y-4">
                {comboResult ? (
                  <>
                    {/* Tank Summary */}
                    <Card className="border-primary/30 bg-primary/5" data-testid="calc-results">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg">Your Tank Mix Recipe</CardTitle>
                        <CardDescription>
                          {comboResult.coverage.num_tank_loads} tank load{comboResult.coverage.num_tank_loads > 1 ? 's' : ''} needed
                          {' '}&middot; {comboResult.coverage.gallons_water_per_tank} gal water each
                          {' '}&middot; Covers {comboResult.coverage.sqft_per_tank.toLocaleString()} sq ft per load
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Per Tank Breakdown */}
                        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Per Tank Load</div>

                        {/* Celsius WG */}
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-background border">
                          <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0">
                            <Flask weight="fill" size={16} className="text-orange-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm">Celsius WG</p>
                            <p className="text-xs text-muted-foreground">Post-emergent herbicide</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg text-orange-600">{comboResult.celsius_wg.per_tank.grams}g</p>
                            <p className="text-xs text-muted-foreground">{comboResult.celsius_wg.per_tank.oz} oz</p>
                          </div>
                        </div>

                        {/* Certainty */}
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-background border">
                          <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                            <Eyedropper weight="fill" size={16} className="text-green-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm">Certainty</p>
                            <p className="text-xs text-muted-foreground">Nutsedge &amp; sedge killer</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg text-green-600">{comboResult.certainty.per_tank.grams}g</p>
                            <p className="text-xs text-muted-foreground">
                              {comboResult.certainty.large_scoops_per_tank} lg scoops &middot; {comboResult.certainty.small_scoops_per_tank} sm scoops
                            </p>
                          </div>
                        </div>

                        {/* Surfactant */}
                        {comboResult.surfactant.included && (
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-background border">
                            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                              <Drop weight="fill" size={16} className="text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm">Non-Ionic Surfactant</p>
                              <p className="text-xs text-muted-foreground">Helps herbicide stick to leaves</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-lg text-blue-600">{comboResult.surfactant.per_tank_tsp} tsp</p>
                              <p className="text-xs text-muted-foreground">{comboResult.surfactant.per_tank_fl_oz} fl oz</p>
                            </div>
                          </div>
                        )}

                        {/* Dye */}
                        {comboResult.dye.included && (
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-background border">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                              <Eyedropper weight="fill" size={16} className="text-indigo-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm">Lazer Blue Dye</p>
                              <p className="text-xs text-muted-foreground">See where you sprayed</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-lg text-indigo-600">{comboResult.dye.per_tank_fl_oz} fl oz</p>
                            </div>
                          </div>
                        )}

                        {/* Water */}
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-background border">
                          <div className="w-8 h-8 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center flex-shrink-0">
                            <Drop weight="fill" size={16} className="text-cyan-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm">Water</p>
                            <p className="text-xs text-muted-foreground">Fill sprayer, add products, top off</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg text-cyan-600">{comboResult.coverage.gallons_water_per_tank} gal</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Total Product Needed */}
                    <Card data-testid="calc-total-summary">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Total Needed ({areaSqft} sq ft)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="p-2 rounded bg-muted/50">
                            <p className="text-muted-foreground">Celsius WG</p>
                            <p className="font-bold">{comboResult.celsius_wg.total.grams}g ({comboResult.celsius_wg.total.oz} oz)</p>
                          </div>
                          <div className="p-2 rounded bg-muted/50">
                            <p className="text-muted-foreground">Certainty</p>
                            <p className="font-bold">{comboResult.certainty.total.grams}g ({comboResult.certainty.total.oz} oz)</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Tips */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Info weight="duotone" size={16} className="text-primary" />
                          Mixing Tips
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-1.5">
                          {comboResult.tips.map((tip, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm">
                              <CheckCircle weight="fill" size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-muted-foreground">{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <Card className="flex flex-col items-center justify-center py-16 text-center">
                    <Flask weight="duotone" size={64} className="text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground font-medium">Enter your lawn area to get started</p>
                    <p className="text-sm text-muted-foreground mt-1">Your exact mix recipe will appear here</p>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ============ FERTILIZER CALCULATOR ============ */}
          <TabsContent value="fertilizer">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator weight="duotone" size={22} className="text-primary" />
                    Fertilizer Rate Calculator
                  </CardTitle>
                  <CardDescription>
                    Enter your soil test results and bag analysis to get exact application rates
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div>
                    <Label>Lawn Area (sq ft)</Label>
                    <Input data-testid="fert-area" type="number" min="100" step="100" value={fertArea} onChange={(e) => setFertArea(e.target.value)} placeholder="e.g., 5000" className="mt-1" />
                  </div>

                  <div>
                    <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Target Rate (lbs per 1,000 sq ft)</Label>
                    <div className="grid grid-cols-3 gap-3 mt-1">
                      <div>
                        <Label className="text-xs">Nitrogen (N)</Label>
                        <Input data-testid="fert-target-n" type="number" min="0" step="0.25" value={fertN} onChange={(e) => setFertN(e.target.value)} placeholder="1.0" className="mt-0.5" />
                      </div>
                      <div>
                        <Label className="text-xs">Phosphorus (P)</Label>
                        <Input data-testid="fert-target-p" type="number" min="0" step="0.25" value={fertP} onChange={(e) => setFertP(e.target.value)} placeholder="0" className="mt-0.5" />
                      </div>
                      <div>
                        <Label className="text-xs">Potassium (K)</Label>
                        <Input data-testid="fert-target-k" type="number" min="0" step="0.25" value={fertK} onChange={(e) => setFertK(e.target.value)} placeholder="0" className="mt-0.5" />
                      </div>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">Standard warm-season rate: 1 lb N per 1,000 sq ft per application</p>
                  </div>

                  <div>
                    <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Your Fertilizer Bag Analysis (N-P-K %)</Label>
                    <div className="grid grid-cols-3 gap-3 mt-1">
                      <div>
                        <Label className="text-xs">N %</Label>
                        <Input data-testid="fert-bag-n" type="number" min="0" max="100" step="1" value={fertBagN} onChange={(e) => setFertBagN(e.target.value)} placeholder="e.g., 24" className="mt-0.5" />
                      </div>
                      <div>
                        <Label className="text-xs">P %</Label>
                        <Input data-testid="fert-bag-p" type="number" min="0" max="100" step="1" value={fertBagP} onChange={(e) => setFertBagP(e.target.value)} placeholder="e.g., 0" className="mt-0.5" />
                      </div>
                      <div>
                        <Label className="text-xs">K %</Label>
                        <Input data-testid="fert-bag-k" type="number" min="0" max="100" step="1" value={fertBagK} onChange={(e) => setFertBagK(e.target.value)} placeholder="e.g., 11" className="mt-0.5" />
                      </div>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">Found on the front of your fertilizer bag (e.g., 24-0-11)</p>
                  </div>

                  <div>
                    <Label>Bag Size (lbs)</Label>
                    <Input data-testid="fert-bag-weight" type="number" min="1" step="1" value={fertBagWeight} onChange={(e) => setFertBagWeight(e.target.value)} className="mt-1" />
                  </div>

                  <Button onClick={calculateFertilizer} disabled={!fertArea || !fertBagN} className="w-full" size="lg" data-testid="fert-calc-btn">
                    <Calculator weight="bold" size={20} className="mr-2" />
                    Calculate Rate
                  </Button>
                </CardContent>
              </Card>

              <div className="space-y-4">
                {fertResult ? (
                  <>
                    <Card className="border-primary/30 bg-primary/5" data-testid="fert-results">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg">Your Application Rate</CardTitle>
                        <CardDescription>
                          {fertResult.bagAnalysis} fertilizer on {fertResult.area.toLocaleString()} sq ft
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 rounded-lg bg-background border text-center">
                            <p className="text-3xl font-bold text-primary">{fertResult.productPer1K}</p>
                            <p className="text-xs text-muted-foreground mt-1">lbs per 1,000 sq ft</p>
                          </div>
                          <div className="p-4 rounded-lg bg-background border text-center">
                            <p className="text-3xl font-bold text-primary">{fertResult.totalProduct}</p>
                            <p className="text-xs text-muted-foreground mt-1">lbs total needed</p>
                          </div>
                        </div>
                        <div className="p-3 rounded-lg bg-background border text-center">
                          <p className="text-xl font-bold">{fertResult.bagsNeeded}</p>
                          <p className="text-xs text-muted-foreground">bag{fertResult.bagsNeeded !== 1 ? 's' : ''} needed ({fertBagWeight} lb bags)</p>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          <p className="font-semibold mb-1">Actual nutrients applied per 1,000 sq ft:</p>
                          <div className="flex gap-4">
                            <span>N: {fertResult.actualN} lb</span>
                            <span>P: {fertResult.actualP} lb</span>
                            <span>K: {fertResult.actualK} lb</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Info weight="duotone" size={16} className="text-primary" />
                          Fertilizer Tips
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-1.5">
                          {[
                            "Apply when grass is actively growing (not dormant)",
                            "Water in within 24 hours of application",
                            "Don't apply more than 1 lb N per 1,000 sq ft per application",
                            "Wait 4-6 weeks between applications",
                            "Skip phosphorus unless soil test shows deficiency",
                            "Use a calibrated spreader for even coverage",
                          ].map((tip, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm">
                              <CheckCircle weight="fill" size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-muted-foreground">{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <Card className="flex flex-col items-center justify-center py-16 text-center">
                    <Calculator weight="duotone" size={64} className="text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground font-medium">Enter your bag analysis to get started</p>
                    <p className="text-sm text-muted-foreground mt-1">Find the N-P-K numbers on the front of your fertilizer bag</p>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ============ SPOON-FEED (pro-grade N planner) ============ */}
          <TabsContent value="spoonfeed">
            <SpoonFeedCalculator defaultSqft={areaSqft} />
          </TabsContent>

          {/* ============ SHOPPING LIST ============ */}
          <TabsContent value="shopping">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart weight="duotone" size={22} className="text-primary" />
                  Everything You Need
                </CardTitle>
                <CardDescription>
                  Complete shopping list for the Celsius + Certainty combo kit
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* FTC-required disclosure positioned ABOVE the affiliate shopping list */}
                <div className="mb-4 rounded-md bg-muted/40 border border-border/60 px-3 py-2">
                  <AmazonDisclosure variant="inline" />
                </div>
                <div className="space-y-4">
                  {/* Celsius WG */}
                  <ShoppingItem
                    name="Celsius WG Herbicide"
                    description="Post-emergent for 150+ weed types on warm-season turf"
                    price="$69.99"
                    size="10 oz bottle"
                    link="https://www.amazon.com/s?k=celsius+wg+herbicide+bayer"
                    essential
                  />
                  {/* Certainty */}
                  <ShoppingItem
                    name="Certainty Turf Herbicide"
                    description="Nutsedge, kyllinga, and Poa annua control"
                    price="$49.99"
                    size="1.25 oz packet"
                    link="https://www.amazon.com/s?k=certainty+turf+herbicide+sulfosulfuron"
                    essential
                  />
                  {/* Surfactant */}
                  <ShoppingItem
                    name="Southern Ag Non-Ionic Surfactant"
                    description="Helps herbicide stick and penetrate waxy leaf surfaces"
                    price="$12.99"
                    size="16 oz bottle"
                    link={adjuncts.find(a => a.id === 'adjunct-nis-surfactant')?.affiliate_link || 'https://www.amazon.com/s?k=southern+ag+non-ionic+surfactant'}
                    essential
                  />
                  {/* Dye */}
                  <ShoppingItem
                    name="Liquid Harvest Lazer Blue"
                    description="Spray indicator dye — see where you sprayed, prevent overlaps"
                    price="$14.99"
                    size="32 oz bottle"
                    link={adjuncts.find(a => a.id === 'adjunct-lazer-blue')?.affiliate_link || 'https://www.amazon.com/s?k=liquid+harvest+lazer+blue+spray+indicator+dye'}
                  />
                  {/* Sprayer */}
                  <ShoppingItem
                    name={SPRAYER_NAME}
                    description="Recommended 4-gallon backpack sprayer with adjustable nozzle"
                    price="~$70"
                    size="4 gallon"
                    link={SPRAYER_LINK}
                    isSprayer
                  />
                  {/* Digital Scale */}
                  <ShoppingItem
                    name="Digital Gram Scale (0.01g)"
                    description="Essential for measuring Celsius and Certainty accurately. Don't eyeball it."
                    price="~$12"
                    size=""
                    link="https://www.amazon.com/s?k=digital+gram+scale+0.01g+precision"
                  />
                </div>

                {/* Affiliate Disclosure */}
                <div className="mt-6 p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                  <strong>Disclosure:</strong> Some links are affiliate links. TurfPulse may earn a small commission at no extra cost to you. We only recommend products we actually use.
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Shopping item component
function ShoppingItem({ name, description, price, size, link, essential, isSprayer }) {
  return (
    <div className={`flex items-center gap-4 p-4 rounded-lg border ${essential ? 'border-primary/30 bg-primary/5' : 'border-border'}`} data-testid={`shop-${name.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-sm">{name}</p>
          {essential && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">Essential</span>}
          {isSprayer && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-medium">Recommended</span>}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        {size && <p className="text-xs text-muted-foreground">{size}</p>}
      </div>
      <div className="text-right flex-shrink-0">
        <p className="font-bold text-sm">{price}</p>
        {link && (
          <a href={link} target="_blank" rel="nofollow sponsored noopener noreferrer">
            <Button variant="outline" size="sm" className="mt-1 text-xs">
              Buy <ArrowRight size={12} className="ml-1" />
            </Button>
          </a>
        )}
      </div>
    </div>
  );
}

export default MixCalculatorPage;
